import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { useAuth } from '../../utils/auth/AuthContext';
import LoginForm from '../../components/auth/LoginForm';

const LoginPage: React.FC = () => {
  const { theme, isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [isProcessingSSO, setIsProcessingSSO] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    if (isAuthenticated) {
      navigate('/');
      return;
    }

    // Check for SSO callback token in URL
    const token = searchParams.get('token');
    if (token) {
      setIsProcessingSSO(true);
      login(token)
        .then(() => {
          navigate('/');
        })
        .catch((error) => {
          console.error('SSO login failed:', error);
          setIsProcessingSSO(false);
          // Remove token from URL to show login form
          window.history.replaceState({}, document.title, '/login');
        });
    }
  }, [searchParams, login, navigate, isAuthenticated]);

  if (isProcessingSSO) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: theme.background,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: theme.text, fontSize: 18, marginBottom: 16 }}>
            Completing SSO login...
          </div>
          <div style={{ color: theme.textSecondary }}>
            Please wait while we log you in.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      background: theme.background,
      padding: 24,
    }}>
      <div style={{ marginBottom: 48, textAlign: 'center' }}>
        <img 
          src={isDarkMode ? "/zell-analytics-white-transparent.png" : "/zell-analytics-black-transparent.png"} 
          alt="Zell Analytics Logo" 
          style={{ 
            maxWidth: 480,
            height: 'auto', 
            marginBottom: 24 
          }} 
        />
        <p style={{ color: theme.textSecondary, fontSize: 16 }}>
          Sign in to manage your data pipelines
        </p>
        
        {isLoadingProviders && (
          <div style={{ 
            marginTop: 32,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{
              width: 40,
              height: 40,
              border: `4px solid ${theme.border}`,
              borderTop: `4px solid ${theme.accent}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        )}
      </div>
      
      <LoginForm onLoadingChange={setIsLoadingProviders} />
    </div>
  );
};

export default LoginPage;
